import React, { createContext, useState, useEffect, useContext } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "feather-icons/dist/icons/chevron-up.svg";
import { ReactComponent as FlagEN } from "images/flags/en.svg";
import { ReactComponent as FlagNL } from "images/flags/nl.svg";
import { ReactComponent as FlagGE } from "images/flags/ge.svg";

const LanguageContext = createContext();

export const pageMappings = {
  "/": { nl: "/nl", en: "/en", de: "/de" },
  "/nl": { nl: "/nl", en: "/en", de: "/de" },
  "/en": { nl: "/nl", en: "/en", de: "/de" },
  "/de": { nl: "/nl", en: "/en", de: "/de" },
  "/voorwaarden": { nl: "/voorwaarden", en: "/termsAndCondition", de: "/geschaftsbedingungen" },
  "/termsAndCondition": { nl: "/voorwaarden", en: "/termsAndCondition", de: "/geschaftsbedingungen" },
  "/geschaftsbedingungen": { nl: "/voorwaarden", en: "/termsAndCondition", de: "/geschaftsbedingungen" },
  "/privacybeleid": { nl: "/privacybeleid", en: "/privacyPolicy", de: "/datenschutz" },
  "/privacyPolicy": { nl: "/privacybeleid", en: "/privacyPolicy", de: "/datenschutz" },
  "/datenschutz": { nl: "/privacybeleid", en: "/privacyPolicy", de: "/datenschutz" },
  "/verzekering": { nl: "/verzekering", en: "/insurance", de: "/Versicherung" },
  "/insurance": { nl: "/verzekering", en: "/insurance", de: "/Versicherung" },
  "/Versicherung": { nl: "/verzekering", en: "/insurance", de: "/Versicherung" },
  "/informatie": { nl: "/informatie", en: "/information", de: "/informationen" },
  "/information": { nl: "/informatie", en: "/information", de: "/informationen" },
  "/informationen": { nl: "/informatie", en: "/information", de: "/informationen" },
  "/groepsreizen": { nl: "/groepsreizen", en: "/groups", de: "/gruppe" },
  "/groups": { nl: "/groepsreizen", en: "/groups", de: "/gruppe" },
  "/gruppe": { nl: "/groepsreizen", en: "/groups", de: "/gruppe" },
  "/crew-nl": { nl: "/crew-nl", en: "/crew", de: "/crew-de" },
  "/crew": { nl: "/crew-nl", en: "/crew", de: "/crew-de" },
  "/crew-de": { nl: "/crew-nl", en: "/crew", de: "/crew-de" },
};

/** Detect language from URL path synchronously (critical for crawlers & prerender). */
export function detectLanguageFromPath(pathname = "/") {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;

  if (path === "/" || path === "/nl" || path === "/en" || path === "/de") {
    if (path === "/") return "nl";
    return path.slice(1);
  }

  for (const mapping of Object.values(pageMappings)) {
    for (const lang of ["nl", "en", "de"]) {
      if (mapping[lang] === path) return lang;
    }
  }

  // Case-insensitive fallback for German insurance path variants
  const lower = path.toLowerCase();
  if (lower === "/versicherung") return "de";

  return null;
}

function getInitialLanguage() {
  if (typeof window === "undefined") return "nl";
  const fromPath = detectLanguageFromPath(window.location.pathname);
  if (fromPath) return fromPath;
  try {
    const stored = localStorage.getItem("language");
    if (stored === "nl" || stored === "en" || stored === "de") return stored;
  } catch (_) {}
  return "nl";
}

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage);

  const updateLanguage = (language) => {
    setCurrentLanguage(language);
    try {
      localStorage.setItem("language", language);
    } catch (_) {}
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setCurrentLanguage: updateLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

const Container = tw.div`relative ml-4 inline-block text-left`;
const Trigger = styled.button`
  ${tw`flex items-center justify-between rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none`}
  width: 48px;
  height: 18px;
  margin-bottom: 10px;
  svg {
    ${tw`w-5 h-5`}
    transform: translateY(3px);
  }
`;

const FlagContainer = styled.div`
  ${tw`flex items-center`}
  transform: translateY(2px);
`;

const Menu = tw.div`absolute left-0 mt-1 w-32 rounded-md z-10 shadow-lg bg-white ring-1 ring-black ring-opacity-5`;
const MenuItem = styled.a`
  ${tw`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 no-underline text-gray-700`}
  svg {
    ${tw`w-5 h-5 mr-2`}
  }
`;

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentLanguage, setCurrentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: "nl", name: "Nederlands", flag: <FlagNL /> },
    { code: "en", name: "English", flag: <FlagEN /> },
    { code: "de", name: "Deutsch", flag: <FlagGE /> },
  ];

  useEffect(() => {
    const detected = detectLanguageFromPath(location.pathname);
    if (detected && detected !== currentLanguage) {
      setCurrentLanguage(detected);
    }
  }, [location.pathname, currentLanguage, setCurrentLanguage]);

  const currentPath = location.pathname.replace(/\/$/, "") || "/";
  const pathForLanguage = (newLanguage) =>
    pageMappings[currentPath]?.[newLanguage] || `/${newLanguage}`;

  const handleLanguageSelect = (event, newLanguage) => {
    event.preventDefault();
    const mappedPath = pathForLanguage(newLanguage);
    setCurrentLanguage(newLanguage);
    try {
      localStorage.setItem("language", newLanguage);
      localStorage.setItem("isInternalNavigation", "true");
    } catch (_) {}
    navigate(mappedPath);
    setIsOpen(false);
  };

  return (
    <Container>
      <Trigger
        type="button"
        aria-label="Select language"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FlagContainer>{languages.find((lang) => lang.code === currentLanguage)?.flag}</FlagContainer>
        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Trigger>
      <Menu role="menu" style={{ display: isOpen ? "block" : "none" }}>
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            href={pathForLanguage(lang.code)}
            hrefLang={lang.code}
            role="menuitem"
            onClick={(event) => handleLanguageSelect(event, lang.code)}
          >
            {lang.flag}
            {lang.name}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
};

export default LanguageSelector;
